/**
 * Audio Playback Module
 *
 * Handles playback of audio responses from Gemini Live API.
 *
 * Features:
 * - Queued gapless playback
 * - Barge-in support (interrupt when user speaks)
 * - Volume control
 * - Base64 decoding of Gemini audio
 *
 * @module gemini/audio-playback
 */

import { AudioPlaybackConfig } from './types';

/**
 * Default playback configuration
 */
const DEFAULT_CONFIG: AudioPlaybackConfig = {
  sampleRate: 24000, // Gemini outputs 24kHz audio
  channels: 1,
  bufferSize: 4096,
};

/**
 * Gapless playback configuration
 */
const GAPLESS_CONFIG = {
  /** Minimum chunks to buffer before starting playback (jitter buffer) */
  minBufferChunks: 2,
  /** Small overlap in seconds for crossfade (prevents clicks) */
  crossfadeMs: 3,
  /** Safety margin to prevent scheduling in the past */
  scheduleAheadSec: 0.05,
};

/**
 * Playback state
 */
export type PlaybackState = 'idle' | 'playing' | 'paused' | 'interrupted';

/**
 * TTFA (Time To First Audio) tracking for latency measurement
 */
interface TTFAMetrics {
  /** Timestamp when first audio chunk was queued */
  firstChunkQueuedAt: number | null;
  /** Timestamp when first audio started playing */
  firstAudioPlayedAt: number | null;
  /** Computed TTFA in milliseconds */
  ttfaMs: number | null;
}

/**
 * Callback types for playback events
 */
export interface AudioPlaybackCallbacks {
  /** Called when playback state changes */
  onStateChange?: (state: PlaybackState) => void;
  /** Called when audio queue is empty */
  onQueueEmpty?: () => void;
  /** Called when playback completes */
  onPlaybackComplete?: () => void;
  /** Called on error */
  onError?: (error: Error) => void;
  /** Called when TTFA is measured (first audio starts playing) */
  onTTFAMeasured?: (ttfaMs: number) => void;
}

/**
 * Audio Playback Class
 *
 * Manages playback of audio responses from Gemini.
 * Supports queued playback and barge-in interruption.
 * Supports synchronized stereo recording (mic + assistant in same AudioContext).
 */
export class AudioPlayback {
  private config: AudioPlaybackConfig;
  private callbacks: AudioPlaybackCallbacks;
  private audioContext: AudioContext | null = null;
  private gainNode: GainNode | null = null;
  private state: PlaybackState = 'idle';
  private audioQueue: AudioBuffer[] = [];
  private currentSource: AudioBufferSourceNode | null = null;
  private isProcessing = false;
  private volume = 1.0;
  private isFadingOut = false;

  /** TTFA metrics for latency measurement */
  private ttfaMetrics: TTFAMetrics = {
    firstChunkQueuedAt: null,
    firstAudioPlayedAt: null,
    ttfaMs: null,
  };

  /** Gapless playback: next scheduled start time in AudioContext time */
  private nextStartTime: number = 0;
  /** Gapless playback: all scheduled sources (for cleanup on interrupt) */
  private scheduledSources: AudioBufferSourceNode[] = [];
  /** Gapless playback: whether we've started the scheduling loop */
  private isScheduling = false;
  /** Gapless playback: chunks received before playback started (jitter buffer) */
  private bufferedChunks = 0;

  // ─────────────────────────────────────────────────────────────────────────
  // Synchronized Recording Infrastructure
  // All recording happens in this 24kHz context (no cross-context drift)
  // ─────────────────────────────────────────────────────────────────────────
  private micSource: MediaStreamAudioSourceNode | null = null;
  private merger: ChannelMergerNode | null = null;
  private recordingDestination: MediaStreamAudioDestinationNode | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private recordingChunks: Blob[] = [];

  constructor(config: Partial<AudioPlaybackConfig> = {}, callbacks: AudioPlaybackCallbacks = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.callbacks = callbacks;
  }

  /**
   * Initialize playback system
   */
  async initialize(): Promise<void> {
    try {
      // Create AudioContext for playback
      this.audioContext = new AudioContext({
        sampleRate: this.config.sampleRate,
      });

      // Create gain node for volume control
      this.gainNode = this.audioContext.createGain();
      this.gainNode.gain.value = this.volume;
      this.gainNode.connect(this.audioContext.destination);

      console.log('[AudioPlayback] Initialized');
    } catch (error) {
      console.error('[AudioPlayback] Initialization failed:', error);
      this.callbacks.onError?.(error instanceof Error ? error : new Error('Failed to initialize playback'));
      throw error;
    }
  }

  /**
   * Queue audio data for playback
   * @param base64Data - Base64 encoded PCM audio from Gemini
   */
  async queueAudio(base64Data: string): Promise<void> {
    if (!this.audioContext) {
      throw new Error('AudioPlayback not initialized');
    }

    try {
      // Track TTFA - record when first chunk is queued
      if (this.ttfaMetrics.firstChunkQueuedAt === null) {
        this.ttfaMetrics.firstChunkQueuedAt = performance.now();
        if (process.env.NODE_ENV === 'development') {
          console.log('[AudioPlayback] First audio chunk queued, starting TTFA measurement');
        }
      }

      // Decode base64 to binary
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Convert to AudioBuffer
      const audioBuffer = await this.createAudioBuffer(bytes.buffer);
      this.audioQueue.push(audioBuffer);

      // Start processing queue if not already - ensures first chunk plays immediately
      if (!this.isProcessing) {
        this.processQueue();
      }
    } catch (error) {
      console.error('[AudioPlayback] Failed to queue audio:', error);
      this.callbacks.onError?.(error instanceof Error ? error : new Error('Failed to queue audio'));
    }
  }

  /**
   * Queue raw PCM data (Int16) with gapless scheduling
   * @param pcmData - Raw Int16 PCM data
   */
  async queueRawPCM(pcmData: ArrayBuffer): Promise<void> {
    if (!this.audioContext) {
      throw new Error('AudioPlayback not initialized');
    }

    try {
      // Track TTFA - record when first chunk is queued
      if (this.ttfaMetrics.firstChunkQueuedAt === null) {
        this.ttfaMetrics.firstChunkQueuedAt = performance.now();
        if (process.env.NODE_ENV === 'development') {
          console.log('[AudioPlayback] First audio chunk queued, starting TTFA measurement');
        }
      }

      // Resume if suspended (autoplay policy)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      const audioBuffer = await this.createAudioBuffer(pcmData);
      this.bufferedChunks++;

      // Use gapless scheduling
      this.scheduleBuffer(audioBuffer);
    } catch (error) {
      console.error('[AudioPlayback] Failed to queue PCM:', error);
      this.callbacks.onError?.(error instanceof Error ? error : new Error('Failed to queue PCM'));
    }
  }

  /**
   * Create AudioBuffer from raw PCM data
   */
  private async createAudioBuffer(pcmData: ArrayBuffer): Promise<AudioBuffer> {
    if (!this.audioContext) {
      throw new Error('AudioContext not available');
    }

    // Convert Int16 PCM to Float32
    const int16Array = new Int16Array(pcmData);
    const float32Array = new Float32Array(int16Array.length);

    for (let i = 0; i < int16Array.length; i++) {
      // Convert Int16 [-32768, 32767] to Float32 [-1, 1]
      float32Array[i] = int16Array[i]! / 32768;
    }

    // Create AudioBuffer
    const audioBuffer = this.audioContext.createBuffer(
      this.config.channels,
      float32Array.length,
      this.config.sampleRate
    );

    // Copy data to buffer
    audioBuffer.copyToChannel(float32Array, 0);

    return audioBuffer;
  }

  /**
   * Schedule a buffer for gapless playback
   * Uses precise AudioContext timing to prevent gaps between chunks
   */
  private scheduleBuffer(audioBuffer: AudioBuffer): void {
    if (!this.audioContext || !this.gainNode || this.isFadingOut) {
      return;
    }

    const now = this.audioContext.currentTime;

    // Initialize nextStartTime on first buffer, with small jitter buffer delay
    if (!this.isScheduling) {
      // Wait for minimum chunks before starting (jitter buffer)
      if (this.bufferedChunks < GAPLESS_CONFIG.minBufferChunks) {
        // Queue the buffer for later scheduling
        this.audioQueue.push(audioBuffer);
        return;
      }

      // Start scheduling - add safety margin to prevent scheduling in the past
      this.nextStartTime = now + GAPLESS_CONFIG.scheduleAheadSec;
      this.isScheduling = true;

      // Schedule any queued buffers first (from jitter buffer)
      while (this.audioQueue.length > 0) {
        const queuedBuffer = this.audioQueue.shift()!;
        this.scheduleBufferAtTime(queuedBuffer, this.nextStartTime);
        this.nextStartTime += queuedBuffer.duration;
      }
    }

    // If nextStartTime is in the past, catch up
    if (this.nextStartTime < now) {
      this.nextStartTime = now + GAPLESS_CONFIG.scheduleAheadSec;
    }

    // Schedule this buffer
    this.scheduleBufferAtTime(audioBuffer, this.nextStartTime);
    this.nextStartTime += audioBuffer.duration;
  }

  /**
   * Schedule a single buffer at a specific time
   */
  private scheduleBufferAtTime(audioBuffer: AudioBuffer, startTime: number): void {
    if (!this.audioContext || !this.gainNode) return;

    // Create source node
    const source = this.audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this.gainNode);

    // Track for interruption cleanup
    this.scheduledSources.push(source);
    this.currentSource = source;

    // Cleanup when this source ends
    source.onended = () => {
      const index = this.scheduledSources.indexOf(source);
      if (index > -1) {
        this.scheduledSources.splice(index, 1);
      }
      source.disconnect();

      // If no more scheduled sources, we're idle
      if (this.scheduledSources.length === 0 && this.audioQueue.length === 0 && !this.isFadingOut) {
        this.setState('idle');
        this.callbacks.onQueueEmpty?.();
        this.callbacks.onPlaybackComplete?.();
      }
    };

    // Schedule at precise time for gapless playback
    source.start(startTime);
    this.setState('playing');

    // Measure TTFA on first audio playback
    if (this.ttfaMetrics.firstChunkQueuedAt !== null && this.ttfaMetrics.firstAudioPlayedAt === null) {
      this.ttfaMetrics.firstAudioPlayedAt = performance.now();
      this.ttfaMetrics.ttfaMs = this.ttfaMetrics.firstAudioPlayedAt - this.ttfaMetrics.firstChunkQueuedAt;
      if (process.env.NODE_ENV === 'development') {
        console.log(`[AudioPlayback] TTFA: ${this.ttfaMetrics.ttfaMs.toFixed(2)}ms (first audio playing)`);
      }
      this.callbacks.onTTFAMeasured?.(this.ttfaMetrics.ttfaMs);
    }
  }

  /**
   * Process the audio queue (legacy method, kept for base64 path)
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.state === 'paused' || this.state === 'interrupted') {
      return;
    }

    this.isProcessing = true;

    while (this.audioQueue.length > 0 && this.getState() !== 'interrupted' && !this.isFadingOut) {
      const audioBuffer = this.audioQueue.shift();
      if (!audioBuffer) break;

      await this.playBuffer(audioBuffer);
    }

    this.isProcessing = false;

    if (this.audioQueue.length === 0 && this.getState() !== 'interrupted') {
      this.setState('idle');
      this.callbacks.onQueueEmpty?.();
      this.callbacks.onPlaybackComplete?.();
    }
  }

  /**
   * Play a single audio buffer
   */
  private playBuffer(audioBuffer: AudioBuffer): Promise<void> {
    return new Promise((resolve) => {
      if (!this.audioContext || !this.gainNode || this.isFadingOut) {
        resolve();
        return;
      }

      // Resume if suspended (autoplay policy)
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      // Create source node
      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this.gainNode);

      // Track current source for interruption
      this.currentSource = source;

      // Handle completion
      source.onended = () => {
        source.disconnect(); // Prevent memory leak by disconnecting finished source
        if (this.currentSource === source) {
          this.currentSource = null;
        }
        resolve();
      };

      // Start playback
      source.start();
      this.setState('playing');

      // Measure TTFA on first audio playback
      if (this.ttfaMetrics.firstChunkQueuedAt !== null && this.ttfaMetrics.firstAudioPlayedAt === null) {
        this.ttfaMetrics.firstAudioPlayedAt = performance.now();
        this.ttfaMetrics.ttfaMs = this.ttfaMetrics.firstAudioPlayedAt - this.ttfaMetrics.firstChunkQueuedAt;
        if (process.env.NODE_ENV === 'development') {
          console.log(`[AudioPlayback] TTFA: ${this.ttfaMetrics.ttfaMs.toFixed(2)}ms (first audio playing)`);
        }
        this.callbacks.onTTFAMeasured?.(this.ttfaMetrics.ttfaMs);
      }
    });
  }

  /**
   * Interrupt playback (barge-in)
   * Called when user starts speaking
   */
  interrupt(): void {
    if (this.state !== 'playing') return;

    this.setState('interrupted');

    // Stop all scheduled sources (gapless playback)
    for (const source of this.scheduledSources) {
      try {
        source.stop();
        source.disconnect();
      } catch {
        // May already be stopped
      }
    }
    this.scheduledSources = [];

    // Stop current source (legacy)
    if (this.currentSource) {
      try {
        this.currentSource.stop();
      } catch {
        // May already be stopped
      }
      this.currentSource = null;
    }

    // Clear queue and reset scheduling state
    this.audioQueue = [];
    this.isProcessing = false;
    this.isScheduling = false;
    this.bufferedChunks = 0;
    this.nextStartTime = 0;

    console.log('[AudioPlayback] Interrupted (barge-in)');
  }

  /**
   * Gracefully stop playback with a fade-out
   * Provides smoother audio experience vs hard cut during barge-in
   *
   * @param durationMs - Fade duration in milliseconds (default: 100ms)
   * @returns Promise that resolves when fade-out is complete
   */
  async fadeOutAndStop(durationMs: number = 100): Promise<void> {
    // Reentry guard - prevent stacked timers from multiple interruption events
    if (this.isFadingOut) {
      return Promise.resolve();
    }

    if (this.state !== 'playing' || !this.gainNode || !this.audioContext) {
      // Nothing to fade, just stop
      this.stop();
      return;
    }

    // Prevent new audio from being queued or played during fade
    this.isFadingOut = true;

    // Clear the queue immediately to prevent new audio from playing
    this.audioQueue = [];
    this.isProcessing = false;

    // Calculate fade parameters
    const currentTime = this.audioContext.currentTime;
    const fadeEndTime = currentTime + (durationMs / 1000);

    // Use exponential ramp for more natural fade-out
    // Note: exponentialRampToValueAtTime doesn't accept 0, use small value
    try {
      this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, currentTime);
      this.gainNode.gain.exponentialRampToValueAtTime(0.001, fadeEndTime);
    } catch (error) {
      console.error('[AudioPlayback] Fade ramp failed:', error);
      this.isFadingOut = false;
      this.stop();
      return Promise.resolve();
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`[AudioPlayback] Fading out over ${durationMs}ms`);
    }

    // Wait for fade to complete, then stop
    return new Promise((resolve) => {
      setTimeout(() => {
        // Stop all scheduled sources (gapless playback)
        for (const source of this.scheduledSources) {
          try {
            source.stop();
            source.disconnect();
          } catch {
            // May already be stopped
          }
        }
        this.scheduledSources = [];

        // Stop current source (legacy)
        if (this.currentSource) {
          try {
            this.currentSource.stop();
          } catch {
            // May already be stopped
          }
          this.currentSource = null;
        }

        // Reset gapless scheduling state
        this.isScheduling = false;
        this.bufferedChunks = 0;
        this.nextStartTime = 0;

        // Reset gain to original volume for next playback
        if (this.gainNode) {
          this.gainNode.gain.cancelScheduledValues(this.audioContext!.currentTime);
          this.gainNode.gain.value = this.volume;
        }

        // Reset state
        this.isFadingOut = false;
        this.setState('interrupted');
        if (process.env.NODE_ENV === 'development') {
          console.log('[AudioPlayback] Fade-out complete');
        }
        resolve();
      }, durationMs);
    });
  }

  /**
   * Clear all queued audio without stopping current playback
   */
  clearQueue(): void {
    this.audioQueue = [];
    console.log('[AudioPlayback] Queue cleared');
  }

  /**
   * Stop all playback and clear queue
   */
  stop(): void {
    this.interrupt();
    this.setState('idle');
  }

  /**
   * Pause playback
   */
  pause(): void {
    if (this.audioContext && this.state === 'playing') {
      this.audioContext.suspend();
      this.setState('paused');
    }
  }

  /**
   * Resume playback
   */
  resume(): void {
    if (this.audioContext && this.state === 'paused') {
      this.audioContext.resume();
      this.setState('playing');
      this.processQueue();
    }
  }

  /**
   * Set volume (0-1)
   */
  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.gainNode) {
      this.gainNode.gain.value = this.volume;
    }
  }

  /**
   * Get current volume
   */
  getVolume(): number {
    return this.volume;
  }

  /**
   * Get current playback state
   */
  getState(): PlaybackState {
    return this.state;
  }

  /**
   * Get queue length
   */
  getQueueLength(): number {
    return this.audioQueue.length;
  }

  /**
   * Check if currently playing
   */
  isPlaying(): boolean {
    return this.state === 'playing';
  }

  /**
   * Update state and notify
   */
  private setState(state: PlaybackState): void {
    if (this.state !== state) {
      this.state = state;
      this.callbacks.onStateChange?.(state);
    }
  }

  /**
   * Reset after interruption (ready for new audio)
   */
  reset(): void {
    this.setState('idle');
    this.audioQueue = [];
    this.isProcessing = false;
    this.isFadingOut = false;
    // Reset gapless scheduling state
    this.isScheduling = false;
    this.bufferedChunks = 0;
    this.nextStartTime = 0;
    this.scheduledSources = [];
    // Reset TTFA metrics for next audio session
    this.ttfaMetrics = {
      firstChunkQueuedAt: null,
      firstAudioPlayedAt: null,
      ttfaMs: null,
    };
    console.log('[AudioPlayback] Reset');
  }

  /**
   * Get the last measured TTFA in milliseconds
   * Returns null if no TTFA has been measured yet
   */
  getTTFA(): number | null {
    return this.ttfaMetrics.ttfaMs;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Synchronized Recording Methods
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Mime type candidates for MediaRecorder (in priority order)
   */
  private static readonly MIME_CANDIDATES = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'video/mp4', // Safari sometimes prefers this for audio-only
    'audio/ogg;codecs=opus',
  ];

  /**
   * Select the best supported mime type for recording
   */
  private selectMimeType(): string | null {
    if (typeof MediaRecorder === 'undefined') return null;
    return AudioPlayback.MIME_CANDIDATES.find((m) =>
      MediaRecorder.isTypeSupported(m)
    ) ?? null;
  }

  /** Current recording bitrate (for external access) */
  private recordingBitrate: number = 128000;

  /**
   * Initialize synchronized recording
   * Creates mic source in this AudioContext (browser resamples to 24kHz)
   * and merges with assistant audio for stereo recording.
   *
   * IMPORTANT: Mic audio goes to merger for recording only, NOT to speakers.
   *
   * @param micStream - MediaStream from AudioCapture.getMediaStream()
   * @param bitrate - Audio bitrate in bps (default: 128000 = 128kbps)
   * @returns true if recording was initialized successfully
   */
  async initializeRecording(micStream: MediaStream, bitrate: number = 128000): Promise<boolean> {
    if (!this.audioContext) {
      console.error('[AudioPlayback] Cannot initialize recording: AudioContext not ready');
      return false;
    }

    this.recordingBitrate = bitrate;

    try {
      // Create mic source in THIS context (browser resamples to 24kHz)
      this.micSource = this.audioContext.createMediaStreamSource(micStream);

      // Create merger: ch0 = mic (L), ch1 = assistant (R)
      this.merger = this.audioContext.createChannelMerger(2);
      this.micSource.connect(this.merger, 0, 0); // mic → left channel
      this.gainNode!.connect(this.merger, 0, 1); // assistant → right channel
      // IMPORTANT: Do NOT connect merger to audioContext.destination
      // Mic should not leak to speakers

      // Create recording destination
      this.recordingDestination = this.audioContext.createMediaStreamDestination();
      this.merger.connect(this.recordingDestination);

      // Create MediaRecorder with detected format
      const mimeType = this.selectMimeType();
      if (!mimeType) {
        console.error('[AudioPlayback] No supported MediaRecorder mime type');
        return false;
      }

      this.mediaRecorder = new MediaRecorder(this.recordingDestination.stream, {
        mimeType,
        audioBitsPerSecond: bitrate,
      });

      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          this.recordingChunks.push(e.data);
        }
      };

      console.log(
        `[AudioPlayback] Recording initialized (mimeType=${this.mediaRecorder.mimeType}, bitrate=${bitrate})`
      );
      return true;
    } catch (error) {
      console.error('[AudioPlayback] Recording init failed:', error);
      this.disposeRecording();
      return false;
    }
  }

  /**
   * Get the recording bitrate configured for this session
   */
  getRecordingBitrate(): number {
    return this.recordingBitrate;
  }

  /**
   * Start synchronized recording
   */
  startRecording(): void {
    if (!this.mediaRecorder) {
      console.warn('[AudioPlayback] Cannot start recording: not initialized');
      return;
    }

    this.recordingChunks = [];
    // 1-second timeslice: controls ondataavailable frequency
    // Note: Chunks stay in memory until stopRecording() - not uploaded during call
    // This just ensures smaller memory chunks vs one giant blob at the end
    this.mediaRecorder.start(1000);
    console.log('[AudioPlayback] Recording started');
  }

  /** Timeout for stopRecording() in case onstop never fires (ms) */
  private static readonly STOP_RECORDING_TIMEOUT_MS = 5000;

  /**
   * Stop recording and return the recorded blob
   *
   * IMPORTANT: Returns a Promise because MediaRecorder.stop() fires
   * the final ondataavailable event AFTER stop() returns. We must wait
   * for the onstop event to ensure all chunks are captured.
   *
   * Includes timeout and error handling to prevent Promise from hanging.
   *
   * @returns Promise resolving to Blob with recorded audio, or null if no data/error
   */
  stopRecording(): Promise<Blob | null> {
    return new Promise((resolve) => {
      if (!this.mediaRecorder) {
        console.warn('[AudioPlayback] Cannot stop recording: not initialized');
        resolve(null);
        return;
      }

      // If already stopped, return current chunks
      if (this.mediaRecorder.state === 'inactive') {
        if (this.recordingChunks.length === 0) {
          console.warn('[AudioPlayback] No recording data captured');
          resolve(null);
        } else {
          const blob = new Blob(this.recordingChunks, {
            type: this.mediaRecorder.mimeType,
          });
          resolve(blob);
        }
        return;
      }

      let resolved = false;
      const recorder = this.mediaRecorder;

      // Timeout in case onstop never fires (edge case)
      const timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          console.warn('[AudioPlayback] stopRecording() timed out, returning partial data');
          if (this.recordingChunks.length > 0) {
            resolve(new Blob(this.recordingChunks, { type: recorder.mimeType }));
          } else {
            resolve(null);
          }
        }
      }, AudioPlayback.STOP_RECORDING_TIMEOUT_MS);

      // Handle errors during stop
      recorder.onerror = (event) => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeoutId);
          console.error('[AudioPlayback] Recording error during stop:', event);
          // Return whatever chunks we have
          if (this.recordingChunks.length > 0) {
            resolve(new Blob(this.recordingChunks, { type: recorder.mimeType }));
          } else {
            resolve(null);
          }
        }
      };

      // Wait for onstop event to capture final chunk
      recorder.onstop = () => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeoutId);

          if (this.recordingChunks.length === 0) {
            console.warn('[AudioPlayback] No recording data captured');
            resolve(null);
            return;
          }

          const blob = new Blob(this.recordingChunks, {
            type: recorder.mimeType,
          });

          console.log(
            `[AudioPlayback] Recording stopped (size=${blob.size}, type=${blob.type})`
          );

          resolve(blob);
        }
      };

      recorder.stop();
    });
  }

  /**
   * Get the actual mime type being used for recording
   * (MediaRecorder may override the requested type)
   */
  getRecordingMimeType(): string | null {
    return this.mediaRecorder?.mimeType ?? null;
  }

  /**
   * Check if recording is currently active
   */
  isRecordingActive(): boolean {
    return this.mediaRecorder?.state === 'recording';
  }

  /**
   * Dispose recording resources (but keep playback working)
   */
  disposeRecording(): void {
    if (this.mediaRecorder) {
      try {
        if (this.mediaRecorder.state !== 'inactive') {
          this.mediaRecorder.stop();
        }
      } catch {
        // May already be stopped
      }
      this.mediaRecorder = null;
    }

    if (this.micSource) {
      this.micSource.disconnect();
      this.micSource = null;
    }

    if (this.merger) {
      this.merger.disconnect();
      this.merger = null;
    }

    this.recordingDestination = null;
    this.recordingChunks = [];
    console.log('[AudioPlayback] Recording resources disposed');
  }

  /**
   * Dispose all resources
   */
  dispose(): void {
    this.stop();

    // Dispose recording resources first
    this.disposeRecording();

    if (this.gainNode) {
      this.gainNode.disconnect();
      this.gainNode = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.audioQueue = [];
    console.log('[AudioPlayback] Disposed');
  }
}

/**
 * Utility: Convert base64 to ArrayBuffer
 */
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Utility: Convert ArrayBuffer to base64
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}
